// Commercial funnel derivation — config-native (planDefinition catalogs).
//
// Stages, commercial scope, and funnel metrics come from org plan pipelines.
// Offline fixtures live in constants for tests without an active plan.

import { EVIDENCE_BY_ID, OFFLINE_OPP_STAGES, OFFLINE_CONSULTING_OPP_STAGES } from './constants';
import { businessDaysBetween, diffDays, isAfter } from './dates';
import { classifyAccount } from './crm';
import { selectActiveCommercialAccounts, isSupersededAccount } from './accountCanonical';
import {
  orgAgingMetrics,
  orgCommercialScope,
  orgFunnelMetricLabels,
  orgFunnelMetrics,
  orgMarketAliases,
  orgMarketLabels,
  orgOpportunityStageLabels,
  orgOpportunityStageRank,
  orgOpportunityStages,
  orgPrimaryDeal,
  orgPrimaryDealEnabled,
  orgQualifyStageLabel,
  orgTerminalStageLabel,
} from './orgPlan';

/** True when a record belongs to the given pipeline (legacy market fallback). */
export function recordInPipeline(row, pipelineKey, defaultMarket) {
  if (!row) return false;
  const key = row.pipelineKey;
  if (key) return key === pipelineKey;
  if (defaultMarket && row.market === defaultMarket) return true;
  return !pipelineKey;
}

/**
 * Commercial accounts for funnel counters — driven by commercialScope + marketAliases.
 */
export function selectFunnelCommercialAccounts(state) {
  const scope = orgCommercialScope(state);
  const all = (state.accounts || []).filter((a) => a && !isSupersededAccount(a));
  const labels = orgMarketLabels(state);
  const aliases = orgMarketAliases(state);

  if (scope.mode === 'all') return all;

  if (scope.mode === 'primary_market') {
    const primary = scope.primaryMarketKey || labels[0] || '';
    // Prefer canonical commercial filter when primary is set (Blocks Dubai path).
    const base =
      primary && !scope.includeTierAAlways
        ? selectActiveCommercialAccounts(state.accounts || [])
        : all;
    return base.filter((a) => {
      if (scope.includeTierAAlways && (a.tier === 'Tier A' || a.tier === 'A')) return true;
      return marketInOrgScope(String(a.market || '').trim(), primary ? [primary] : labels, aliases);
    });
  }

  // org_markets
  if (!labels.length) return all;
  return all.filter((a) => {
    if (scope.includeTierAAlways && (a.tier === 'Tier A' || a.tier === 'A')) return true;
    return marketInOrgScope(String(a.market || '').trim(), labels, aliases);
  });
}

/** Match account market to org labels using plan marketAliases (no hardcoded MENA list). */
export function marketInOrgScope(accountMarket, labels, aliases = {}) {
  const m = String(accountMarket || '').trim().toLowerCase();
  if (!m) return true; // unscoped
  const normalizedLabels = labels.map((l) => String(l).trim().toLowerCase()).filter(Boolean);
  if (normalizedLabels.some((l) => l === m || m.includes(l) || l.includes(m))) return true;

  for (const [canonical, aliasList] of Object.entries(aliases || {})) {
    const canon = String(canonical).trim().toLowerCase();
    const inScope = normalizedLabels.some((l) => l === canon || l.includes(canon) || canon.includes(l));
    if (!inScope) continue;
    const aliasesLc = (aliasList || []).map((a) => String(a).trim().toLowerCase());
    if (aliasesLc.some((a) => m === a || m.includes(a) || a.includes(m))) return true;
  }
  return false;
}

/** @deprecated Offline fixture — product path uses orgOpportunityStageLabels(state). */
export const CONSULTING_OPP_STAGES = OFFLINE_CONSULTING_OPP_STAGES;

export const CONSULTING_SEGMENTS = [
  'Bank',
  'PSP',
  'Fintech',
  'Asset manager',
  'Enterprise finance',
  'Other',
];

// Local copies so this module has no cycle back into derive.js.
function num(v) {
  const n = typeof v === 'number' ? v : parseFloat(String(v ?? '').replace(/[, ]/g, ''));
  return Number.isFinite(n) ? n : 0;
}

function weightOf(levelId) {
  return EVIDENCE_BY_ID[levelId]?.weight ?? -1;
}

/* ------------------------------------------------------------ vocabulary - */

export const ACCOUNT_TIERS = ['Universe', 'Qualified Target', 'Tier A', 'Tier B', 'Parked'];

// Account progression. Rank is used for monotone funnel counting: an account
// at Discovery has, by definition, also been contacted and engaged.
export const ACCOUNT_STATUSES = [
  { key: 'Universe', rank: 0, note: 'Identified. No founder attention required yet.' },
  { key: 'Researching', rank: 1, note: 'Being qualified against the ICP.' },
  { key: 'Contacted', rank: 2, note: 'Meaningful outreach completed.' },
  { key: 'Engaged', rank: 3, note: 'Reply, introduction, meeting booked, active back-and-forth. Roughly E1.' },
  { key: 'Discovery', rank: 4, note: 'A substantive commercial conversation occurred.' },
  { key: 'Opportunity', rank: 5, note: 'A real opportunity has been created from this account.' },
  { key: 'Parked', rank: -1, note: 'Timing wrong or no route in. Reason required.' },
  { key: 'Rejected', rank: -1, note: 'Not a fit. Reason required.' },
];

export const ACCOUNT_STATUS_KEYS = ACCOUNT_STATUSES.map((s) => s.key);
export const statusRank = (k) => ACCOUNT_STATUSES.find((s) => s.key === k)?.rank ?? -1;

export const SEGMENTS = [
  'Developer',
  'Asset owner',
  'Portfolio owner',
  'Family office',
  'Fund / investment firm',
  'Sponsor / operator',
  'Regulated institution',
  'Other',
];

/** @deprecated Offline fixture — product path uses orgOpportunityStageLabels(state). */
export const OPP_STAGES = OFFLINE_OPP_STAGES;

export function stageRankMapFor(state) {
  const fromPlan = orgOpportunityStageRank(state);
  if (Object.keys(fromPlan).length) return fromPlan;
  return Object.fromEntries(OFFLINE_OPP_STAGES.map((s, i) => [s, i]));
}

export function stageLabelsFor(state) {
  const fromPlan = orgOpportunityStageLabels(state);
  if (fromPlan.length) return fromPlan;
  return [...OFFLINE_OPP_STAGES];
}

/** CRM-observed shells never count as qualified funnel progress. */
export function isQualifiedOpportunity(o, state) {
  if (!o) return false;
  if (o.qualificationStatus === 'unqualified' || o.stage === 'Observed') return false;
  if (o.qualificationStatus === 'qualified') return true;
  const rank = stageRankMapFor(state);
  const qualifyLabel = state ? orgQualifyStageLabel(state) : 'Qualified';
  const qRank = rank[qualifyLabel] ?? rank.Qualified ?? 1;
  return (rank[o.stage] ?? -1) >= qRank;
}

/** Bridge qualification gate — not CRM naming. */
export function canQualifyOpportunity(o) {
  return !!String(o?.problemConfirmed || '').trim() && !!String(o?.nextAction || '').trim();
}

export function qualifyOpportunityPatch(o, today, state) {
  if (!canQualifyOpportunity(o)) {
    return { ok: false, changes: null, reason: 'Need a confirmed problem/use case and a real next commercial step.' };
  }
  const rank = stageRankMapFor(state);
  const qualifyLabel = state ? orgQualifyStageLabel(state) : 'Qualified';
  const qRank = rank[qualifyLabel] ?? rank.Qualified ?? 1;
  const promote = (rank[o.stage] ?? -1) < qRank;
  return {
    ok: true,
    changes: {
      qualificationStatus: 'qualified',
      stage: promote ? qualifyLabel : o.stage,
      stageSince: promote ? today : o.stageSince,
      history: promote ? [...(o.history || []), { stage: qualifyLabel, date: today }] : o.history || [],
    },
  };
}

/** @deprecated Prefer stageRankMapFor(state). */
export const OPP_STAGE_RANK = Object.fromEntries(OFFLINE_OPP_STAGES.map((s, i) => [s, i]));

export const OPPORTUNITY_TYPES = [
  'Dubai Founding Asset',
  'Blocks Infrastructure',
  'Saudi Contribution OS',
  'Saudi Infrastructure',
  'Regulated Partnership',
  'Other',
];

export const SCREEN_STATUSES = ['Not started', 'In screen', 'Passed', 'Failed', 'On hold'];

/* ------------------------------------------------------- candidate score - */

export const SCORE_CRITERIA = [
  { key: 'sponsorAuthority', label: 'Sponsor authority', help: 'Can this person commit the asset?' },
  { key: 'assetReadiness', label: 'Asset readiness', help: 'How close is the asset to being transactable?' },
  { key: 'titleClarity', label: 'Title / ownership clarity', help: 'Is ownership clean and evidenced?' },
  { key: 'commercialUrgency', label: 'Commercial urgency', help: 'Does the sponsor need this to happen soon?' },
  { key: 'regulatorySimplicity', label: 'Regulatory simplicity', help: 'How few permissions does this route need?' },
  { key: 'executionPartnerFit', label: 'Execution partner fit', help: 'Does a licensed partner want this deal?' },
  { key: 'capitalFit', label: 'Capital / distribution fit', help: 'Would our capital channels accept it?' },
  { key: 'speedToTransaction', label: 'Speed to transaction', help: 'Can it close inside the sprint horizon?' },
  { key: 'revenuePotential', label: 'Blocks revenue potential', help: 'What does this deal pay Blocks?' },
  { key: 'strategicValue', label: 'Repeat / strategic value', help: 'Does it open a repeatable motion?' },
];

export function candidateScore(opp) {
  const s = opp?.scores || {};
  const total = SCORE_CRITERIA.reduce((a, c) => a + Math.max(0, Math.min(5, num(s[c.key]))), 0);
  const scored = SCORE_CRITERIA.filter((c) => num(s[c.key]) > 0).length;
  return { total, max: 50, scored, complete: scored === SCORE_CRITERIA.length };
}

export function scoreProfile(opp) {
  const s = opp?.scores || {};
  const rows = SCORE_CRITERIA.map((c) => ({ ...c, value: num(s[c.key]) }));
  const strengths = rows.filter((r) => r.value >= 4).sort((a, b) => b.value - a.value);
  const risks = rows.filter((r) => r.value > 0 && r.value <= 2).sort((a, b) => a.value - b.value);
  const missing = rows.filter((r) => r.value === 0);
  return { rows, strengths, risks, missing };
}

/* ----------------------------------------------------------- derivation -- */

function countForMetric(metric, counts, qualifiedOpps, rank) {
  const key = metric.key;
  if (key && counts[key] != null) return counts[key];
  const source = metric.source;
  const match = metric.match || key;
  if (source === 'account_status' || source === 'count') {
    if (counts[match] != null) return counts[match];
    if (counts[key] != null) return counts[key];
  }
  if (source === 'opp_stage') {
    const floor = rank[match] ?? -1;
    if (floor < 0) return qualifiedOpps.filter((o) => o.stage === match).length;
    return qualifiedOpps.filter((o) => (rank[o.stage] ?? -1) >= floor).length;
  }
  if (source === 'flag') {
    return qualifiedOpps.filter((o) => !!o[match]).length;
  }
  return counts[key] ?? 0;
}

function targetForMetric(metric, targets) {
  const key = metric.key;
  if (targets[key] != null) return targets[key];
  // Common aliases from funnelHypothesis keys
  if (key === 'discovery' && targets.conversations != null) return targets.conversations;
  if (key === 'qualified' && targets.qualifiedOpportunities != null) return targets.qualifiedOpportunities;
  if (key === 'assetsIdentified' && targets.assets != null) return targets.assets;
  if (key === 'sows' && targets.commercialCommitment != null && targets.sows == null) {
    return targets.commercialCommitment;
  }
  return targets[metric.match];
}

export function deriveFunnel(state, sprint) {
  const primaryDeal = orgPrimaryDeal(state);
  const primaryEnabled = !!primaryDeal?.enabled;
  const scope = orgCommercialScope(state);
  const accounts = selectFunnelCommercialAccounts(state);
  const labels = orgMarketLabels(state);
  const aliases = orgMarketAliases(state);
  const rank = stageRankMapFor(state);
  const terminal = orgTerminalStageLabel(state);
  const primaryMarket =
    scope.mode === 'primary_market' ? scope.primaryMarketKey || labels[0] || '' : '';

  const opps = (state.opportunities || []).filter((o) => {
    if (scope.mode === 'all') return true;
    if (scope.mode === 'primary_market' && primaryMarket) {
      return marketInOrgScope(String(o.market || '').trim() || primaryMarket, [primaryMarket], aliases);
    }
    if (!labels.length) return true;
    return marketInOrgScope(String(o.market || '').trim(), labels, aliases);
  });

  const assets = primaryEnabled ? state.assetCandidates || [] : [];
  const targets = state.settings?.funnelTargets || {};
  const metricLabels = orgFunnelMetricLabels(state);

  const live = (a) => statusRank(a.status) >= 0;
  const atLeast = (key) => accounts.filter((a) => live(a) && statusRank(a.status) >= statusRank(key)).length;

  const liveOpps = opps.filter((o) => o.stage !== terminal && o.stage !== 'Won / Lost / Parked');
  const qualifiedOpps = liveOpps.filter((o) => isQualifiedOpportunity(o, state));
  const oppAt = (stage) =>
    qualifiedOpps.filter((o) => (rank[o.stage] ?? -1) >= (rank[stage] ?? -1)).length;

  const candidateKey = primaryDeal?.candidateKey || 'd001Candidate';
  const primaryKey = primaryDeal?.primaryKey || 'primaryD001';
  const candidates = primaryEnabled ? qualifiedOpps.filter((o) => o[candidateKey]) : [];
  const primary = primaryEnabled ? qualifiedOpps.find((o) => o[primaryKey]) || null : null;
  const backups = primaryEnabled ? candidates.filter((c) => !c[primaryKey]) : [];

  const proposalStages = orgOpportunityStages(state)
    .filter((s) => /proposal|sow|upsell|readiness proposed|readiness active/i.test(s.label || ''))
    .map((s) => s.label);
  const sowStages = orgOpportunityStages(state)
    .filter((s) => /sow|upsell|mandate|live/i.test(s.label || ''))
    .map((s) => s.label);

  const proposals = qualifiedOpps.filter((o) => proposalStages.includes(o.stage)).length;
  const sows = qualifiedOpps.filter((o) => sowStages.includes(o.stage)).length;

  const counts = {
    universe: accounts.length,
    qualifiedTargets: accounts.filter((a) => ['Qualified Target', 'Tier A', 'Tier B'].includes(a.tier)).length,
    tierA: accounts.filter((a) => a.tier === 'Tier A').length,
    tierB: accounts.filter((a) => a.tier === 'Tier B').length,
    contacted: atLeast('Contacted'),
    engaged: atLeast('Engaged'),
    discovery: atLeast('Discovery'),
    observed: liveOpps.filter((o) => o.stage === 'Observed' || o.qualificationStatus === 'unqualified').length,
    qualified: oppAt(orgQualifyStageLabel(state)) || qualifiedOpps.length,
    assetsIdentified: assets.length,
    assetsInScreen: assets.filter((a) => a.screenStatus === 'In screen').length,
    candidates: candidates.length,
    primary: primary ? 1 : 0,
    backups: backups.length,
    proposals,
    sows,
    parked: accounts.filter((a) => !live(a)).length,
  };

  const L = (key, fallback) => metricLabels[key] || fallback;
  const metricDefs = orgFunnelMetrics(state);
  const steps = metricDefs.length
    ? metricDefs.map((m) => ({
        key: m.key,
        label: m.label || L(m.key, m.key),
        count: countForMetric(m, counts, qualifiedOpps, rank),
        target: targetForMetric(m, targets),
      }))
    : [
        { key: 'universe', label: L('universe', 'Accounts researched'), count: counts.universe, target: targets.universe },
        { key: 'qualifiedTargets', label: L('qualifiedTargets', 'Screened accounts'), count: counts.qualifiedTargets, target: targets.qualifiedTargets },
        { key: 'contacted', label: 'Contacted', count: counts.contacted },
        { key: 'engaged', label: 'Engaged', count: counts.engaged },
        { key: 'discovery', label: L('conversations', 'Commercial conversations'), count: counts.discovery, target: targets.conversations },
        { key: 'qualified', label: L('qualifiedOpportunities', 'Real opportunities'), count: counts.qualified, target: targets.qualifiedOpportunities },
      ];

  const priorityQueue = {
    count: counts.tierA,
    targetMin: 10,
    targetMax: targets.tierA ?? 15,
    label: L('tierA', 'Priority accounts'),
  };

  const evidence = state.evidence || [];
  const evidenceMarket = primaryMarket || '';
  const dubaiE4 = primaryEnabled
    ? evidence.filter((e) => (!evidenceMarket || e.market === evidenceMarket) && e.level === 'E4').length
    : 0;
  const dubaiE5 = primaryEnabled
    ? evidence.filter((e) => (!evidenceMarket || e.market === evidenceMarket) && e.level === 'E5').length
    : 0;

  const conversions = primaryEnabled
    ? [
        conv('Universe → Qualified target', counts.universe, counts.qualifiedTargets),
        conv('Qualified target → Contacted', counts.qualifiedTargets, counts.contacted),
        conv('Contacted → Engaged', counts.contacted, counts.engaged),
        conv('Engaged → Discovery', counts.engaged, counts.discovery),
        conv('Discovery → Qualified opportunity', counts.discovery, counts.qualified),
        conv('Qualified → Asset identified', counts.qualified, counts.assetsIdentified),
        conv('Asset identified → D001 candidate', counts.assetsIdentified, counts.candidates),
        conv('D001 candidate → Primary D001', counts.candidates, counts.primary),
        conv('Primary D001 → E4', counts.primary, dubaiE4),
        conv('E4 → E5', dubaiE4, dubaiE5),
      ]
    : [
        conv('Universe → Qualified target', counts.universe, counts.qualifiedTargets),
        conv('Qualified target → Contacted', counts.qualifiedTargets, counts.contacted),
        conv('Contacted → Engaged', counts.contacted, counts.engaged),
        conv('Engaged → Discovery', counts.engaged, counts.discovery),
        conv('Discovery → Qualified opportunity', counts.discovery, counts.qualified),
        conv('Qualified → Proposal', counts.qualified, counts.proposals),
        conv('Proposal → SOW', counts.proposals, counts.sows),
      ];

  const aging = deriveAging(state, sprint, { accounts, opps: liveOpps, primaryEnabled });
  const health = funnelHealth(counts, primary, backups, !primaryEnabled);
  const concentration = concentrationRisk(counts, primary, backups, dubaiE4 + dubaiE5, !primaryEnabled);
  const diagnosis = diagnoseFunnel(counts, { dubaiE4, primary, backups, consulting: !primaryEnabled });
  const velocity = deriveVelocity(state);
  const projection = projectCandidates(counts, sprint, conversions);
  const queue = founderQueue(state, sprint, { accounts, opps: liveOpps });

  return {
    accounts,
    opps,
    liveOpps,
    assets,
    candidates,
    primary,
    backups,
    counts,
    steps,
    priorityQueue,
    conversions,
    aging,
    health,
    concentration,
    diagnosis,
    velocity,
    projection,
    queue,
    qualify: qualificationCandidates(accounts),
    tierBCount: counts.tierB,
    promotable: promotionCandidates(state, { accounts, opps: liveOpps }),
    dubaiE4,
    dubaiE5,
    targets,
    consulting: !primaryEnabled,
    primaryDealEnabled: primaryEnabled,
  };
}

function conv(label, from, to) {
  const rate = from > 0 ? Math.round((to / from) * 100) : null;
  return { label, from, to, rate };
}

/* --------------------------------------------------------------- health -- */

export function funnelHealth(counts, primary, backups, consulting = false) {
  if (consulting) {
    if (counts.sows >= 1 && counts.proposals >= 1)
      return { status: 'Strong', why: 'A SOW path is open with proposal density behind it.' };
    if (counts.proposals >= 1 || (counts.discovery >= 3 && counts.qualified >= 1))
      return { status: 'Adequate', why: 'Discoveries and proposals are moving toward a package SOW.' };
    if (counts.discovery >= 1 || counts.qualified >= 1)
      return { status: 'Thin', why: 'Some discovery exists but the package wedge is under-supplied.' };
    return { status: 'Critical', why: 'No credible discovery or proposal path toward a package SOW.' };
  }
  const upstream = counts.qualified;
  if (primary && backups.length >= 1 && upstream >= 3)
    return { status: 'Strong', why: 'A Primary D001, at least one credible backup, and qualified opportunities still moving behind them.' };
  if ((primary && backups.length >= 1) || counts.candidates >= 2)
    return { status: 'Adequate', why: 'Two credible candidates, or a Primary with an active qualified backup. Enough to choose rather than attach.' };
  if (counts.candidates >= 1 || counts.qualified >= 1)
    return { status: 'Thin', why: 'One promising opportunity and little behind it. A single stall would empty the funnel.' };
  return { status: 'Critical', why: 'No credible asset or opportunity is approaching D001. The first-deal engine is under-supplied.' };
}

export function concentrationRisk(counts, primary, backups, dubaiProof, consulting = false) {
  if (consulting) {
    if ((counts.qualified || 0) === 0 && (counts.discovery || 0) === 0) {
      return {
        level: 'Not assessable',
        why: 'No discovery or qualified opportunity exists. The issue is funnel supply, not concentration.',
      };
    }
    if (counts.proposals <= 1 && counts.sows === 0)
      return { level: 'High', why: 'Few proposal paths — a single stall would empty the SOW path.' };
    if (counts.proposals >= 2 || counts.discovery >= 3)
      return { level: 'Low', why: 'Multiple discoveries / proposals absorb a failure.' };
    return { level: 'Medium', why: 'Package wedge density is limited.' };
  }
  if ((counts.qualified || 0) === 0) {
    return {
      level: 'Not assessable',
      why: 'No qualified opportunity exists. The issue is funnel supply, not concentration.',
    };
  }
  if (counts.qualified <= 1 && dubaiProof === 0)
    return { level: 'Critical', why: 'Only one meaningful opportunity exists and it has not reached E4.' };
  if (primary && backups.length === 0)
    return { level: 'High', why: 'A Primary D001 exists with no qualified backup. If the sponsor stalls, the sprint stalls.' };
  if (primary && backups.length === 1)
    return { level: 'Medium', why: 'Primary plus one credible backup.' };
  if (counts.candidates >= 2 || counts.qualified >= 3)
    return { level: 'Low', why: 'Multiple credible candidates and upstream opportunities.' };
  return { level: 'High', why: 'Too few credible opportunities to absorb a failure.' };
}

/* ------------------------------------------------------------ diagnosis -- */

export function diagnoseFunnel(c, extra = {}) {
  const out = [];
  const { dubaiE4 = 0, primary = null, backups = [], consulting = false } = extra;
  const priority = c.tierA || 0;
  const readyPool = Math.max(priority, c.qualifiedTargets || 0);
  const contacted = c.contacted || 0;
  const engaged = c.engaged || 0;
  const discovery = c.discovery || 0;

  // Conversion cannot be assessed until deliberate outreach reaches sufficient volume.
  if (readyPool >= 1 && contacted < 3) {
    out.push({
      key: 'outreach_insufficient',
      stage: 'Constraint not yet assessable',
      text: 'Constraint not yet assessable — outreach has not started at sufficient volume.',
    });
  } else if (contacted >= 3 && engaged / Math.max(1, contacted) < 0.25) {
    out.push({
      key: 'access',
      stage: 'Access / outreach conversion',
      text: `${contacted} accounts contacted and ${engaged} engaged. Access / outreach conversion appears to be the current problem.`,
    });
  } else if (engaged >= 3 && discovery / Math.max(1, engaged) < 0.35) {
    out.push({
      key: 'conversation',
      stage: 'Conversation conversion',
      text: `${engaged} engagements produced ${discovery} commercial conversation${discovery === 1 ? '' : 's'}. Conversation conversion appears to be the current problem.`,
    });
  }

  if (discovery >= 4 && c.qualified / Math.max(1, discovery) < 0.35)
    out.push({
      key: 'icp',
      stage: 'Qualification',
      text: `${discovery} commercial conversations produced ${c.qualified} real opportunit${c.qualified === 1 ? 'y' : 'ies'}. The issue appears to be ICP or value proposition rather than top-of-funnel volume.`,
    });

  if (consulting) {
    if (c.discovery >= 2 && (c.proposals || 0) === 0)
      out.push({
        key: 'proposal',
        stage: 'Proposal conversion',
        text: 'Discoveries exist but no proposals issued. Commercial next steps are the constraint.',
      });
    if ((c.proposals || 0) >= 1 && (c.sows || 0) === 0)
      out.push({
        key: 'sow',
        stage: 'SOW conversion',
        text: 'Proposals exist but no SOW / deposit path yet. Close discipline is the constraint.',
      });
    return out;
  }

  if (c.qualified >= 2 && c.assetsIdentified === 0)
    out.push({
      key: 'commitment',
      stage: 'Asset commitment',
      text: 'Real opportunities exist but no assets have been shared. Commercial interest is not converting into commitment — investigate trust, sponsor authority, or the value of the next step.',
    });

  if (c.assetsIdentified >= 2 && c.candidates === 0)
    out.push({
      key: 'assetQuality',
      stage: 'Asset quality',
      text: 'Assets have been identified but none has become a D001 candidate. Asset quality or execution complexity may be the constraint.',
    });

  if (c.candidates >= 1 && dubaiE4 === 0)
    out.push({
      key: 'conversion',
      stage: 'Commercial conversion',
      text: 'D001 candidates exist but no paid proposal or binding commercial commitment yet. The issue has moved from origination to commercial conversion.',
    });

  if (primary && backups.length === 0)
    out.push({
      key: 'concentration',
      stage: 'Concentration',
      text: 'Deal concentration risk is high. Maintain at least one credible backup until D001 reaches binding proof.',
    });

  return out;
}

/* --------------------------------------------------------------- aging --- */

export function deriveAging(state, sprint, { accounts, opps, consulting = false, primaryEnabled = !consulting }) {
  const th = state.settings?.agingThresholds || {};
  const out = [];
  const today = sprint.executionAsOf;
  if (!today) return out;
  const deal = orgPrimaryDeal(state);
  const candidateKey = deal?.candidateKey || 'd001Candidate';
  const candidateLabel = deal?.label ? `${deal.label} candidate` : 'Primary-deal candidate';
  const agingDefs = orgAgingMetrics(state);
  const hasCandidateAging = !agingDefs.length || agingDefs.some((m) => m.key === 'd001Candidate' || /candidate/i.test(m.key));

  for (const a of accounts) {
    if (a.tier !== 'Tier A') continue;
    if (statusRank(a.status) < 0) continue;
    const overdue = a.nextActionDue && isAfter(today, a.nextActionDue);
    const idle = a.stageSince ? businessDaysBetween(a.stageSince, today) : null;
    if (a.status === 'Contacted' && ((idle !== null && idle > (th.tierAContacted ?? 3)) || overdue)) {
      out.push({
        id: `age-acct-${a.id}`,
        kind: 'account',
        ref: a.id,
        severity: 'medium',
        label: `${a.name || 'Unnamed account'} — Tier A contacted, no movement`,
        detail: `${idle === null ? 'No stage date recorded' : `${idle} business days in Contacted`}${overdue ? ', next action overdue' : ''}.`,
      });
    }
    if (a.status === 'Discovery' && a.nextActionDue && businessDaysBetween(a.nextActionDue, today) > (th.discoveryNextAction ?? 1)) {
      out.push({
        id: `age-disc-${a.id}`,
        kind: 'account',
        ref: a.id,
        severity: 'high',
        label: `${a.name || 'Unnamed account'} — discovery completed, next action overdue`,
        detail: 'A discovery conversation without a next step decays fast.',
      });
    }
  }

  for (const o of opps) {
    const idle = o.stageSince ? businessDaysBetween(o.stageSince, today) : null;
    if (idle === null) continue;
    if (o.stage === 'Qualified' && idle > (th.qualifiedOpportunity ?? 5))
      out.push({
        id: `age-opp-${o.id}`,
        kind: 'opportunity',
        ref: o.id,
        severity: 'medium',
        label: `${o.name || 'Unnamed opportunity'} — qualified but static`,
        detail: `${idle} business days without movement.`,
      });
    if (primaryEnabled && hasCandidateAging && (o[candidateKey] || /candidate/i.test(o.stage || '')) && idle > (th.d001Candidate ?? 3))
      out.push({
        id: `age-cand-${o.id}`,
        kind: 'opportunity',
        ref: o.id,
        severity: 'high',
        label: `${o.name || 'Unnamed candidate'} — ${candidateLabel} not progressing`,
        detail: `${idle} business days without meaningful progress.`,
      });
    if (!primaryEnabled && o.stage === 'Proposal' && idle > (th.qualifiedOpportunity ?? 5))
      out.push({
        id: `age-prop-${o.id}`,
        kind: 'opportunity',
        ref: o.id,
        severity: 'high',
        label: `${o.name || 'Unnamed opportunity'} — proposal not progressing to SOW`,
        detail: `${idle} business days without meaningful progress.`,
      });
  }

  return out;
}

/* ------------------------------------------------------------ velocity --- */

// Median elapsed days between recorded stage transitions. History entries are
// written whenever a stage changes; with fewer than three transitions we say so
// rather than inventing a number.
export function deriveVelocity(state) {
  const spans = {};
  const record = (from, to, days) => {
    const k = `${from} → ${to}`;
    spans[k] = spans[k] || [];
    spans[k].push(days);
  };

  for (const list of [state.accounts || [], state.opportunities || []]) {
    for (const item of list) {
      const h = [...(item.history || [])].sort((a, b) => (a.date < b.date ? -1 : 1));
      for (let i = 1; i < h.length; i += 1) {
        const days = diffDays(h[i - 1].date, h[i].date);
        // Only forward moves between different stages tell you anything about
        // how fast the funnel actually runs.
        if (days >= 0 && h[i - 1].stage !== h[i].stage) record(h[i - 1].stage, h[i].stage, days);
      }
    }
  }

  const rows = Object.entries(spans).map(([label, days]) => ({
    label,
    n: days.length,
    median: median(days),
  }));

  const transitions = rows.reduce((a, r) => a + r.n, 0);
  return {
    rows: rows.sort((a, b) => b.n - a.n),
    transitions,
    enough: transitions >= 3,
    note: transitions >= 3 ? null : 'Not enough data yet.',
  };
}

export function median(values) {
  if (!values.length) return null;
  const v = [...values].sort((a, b) => a - b);
  const mid = Math.floor(v.length / 2);
  return v.length % 2 ? v[mid] : Math.round(((v[mid - 1] + v[mid]) / 2) * 10) / 10;
}

/* ---------------------------------------------------------- projection --- */

// Directional only, and suppressed unless there is a real basis for it.
export function projectCandidates(counts, sprint, conversions) {
  const qualToAsset = conversions.find((c) => c.label.startsWith('Qualified → Asset'));
  const assetToCand = conversions.find((c) => c.label.startsWith('Asset identified → D001'));
  const enough = counts.qualified >= 3 && counts.assetsIdentified >= 1 && sprint.day >= 7;
  if (!enough) return { available: false, note: 'Not enough data yet.' };

  const r1 = (qualToAsset?.rate ?? 0) / 100;
  const r2 = (assetToCand?.rate ?? 0) / 100;
  const inFlight = Math.max(0, counts.qualified - counts.assetsIdentified);
  const daysToDay30 = Math.max(0, 30 - sprint.day);
  const timeFactor = Math.min(1, daysToDay30 / Math.max(1, sprint.day));
  const projected = counts.candidates + inFlight * r1 * r2 * timeFactor;

  return {
    available: true,
    value: Math.round(projected * 10) / 10,
    text: `At the current conversion rate and stage velocity, the pipeline is trending toward ${Math.round(projected * 10) / 10} credible D001 candidates by Day 30.`,
    caveat: 'Directional estimate — not a forecast guarantee.',
  };
}

/* -------------------------------------------------------- founder queue -- */

// The founder works this list, not the universe. Ranked by what actually
// changes the odds of a first transaction.
export function founderQueue(state, sprint, { accounts, opps }, limit = 15) {
  const rows = [];
  const accById = Object.fromEntries(accounts.map((a) => [a.id, a]));
  const asOf = sprint.executionAsOf;
  const primaryEnabled = orgPrimaryDealEnabled(state);
  const deal = orgPrimaryDeal(state);
  const primaryKey = deal?.primaryKey || 'primaryD001';
  const candidateKey = deal?.candidateKey || 'd001Candidate';
  const rank = stageRankMapFor(state);

  for (const o of opps) {
    const acc = accById[o.accountId];
    if (acc && acc.tier === 'Parked') continue;
    const overdue = !!(asOf && o.nextActionDue && isAfter(asOf, o.nextActionDue));
    const idle = asOf && o.stageSince ? businessDaysBetween(o.stageSince, asOf) : 0;
    const score =
      (rank[o.stage] ?? 0) * 6 +
      Math.max(0, weightOf(o.evidenceLevel)) * 5 +
      (primaryEnabled
        ? o[primaryKey]
          ? 30
          : o[candidateKey]
            ? 18
            : 0
        : o.stage === 'Sprint SOW' || o.stage === 'Proposal'
          ? 24
          : 0) +
      (o.decisionMaker ? 4 : 0) +
      (o.budgetSignal ? 5 : 0) +
      (overdue ? 12 : 0) +
      Math.min(idle, 10);
    rows.push({
      key: `opp-${o.id}`,
      kind: 'opportunity',
      id: o.id,
      account: acc?.name || o.accountName || '—',
      title: o.name || 'Unnamed opportunity',
      stage: o.stage,
      evidenceLevel: o.evidenceLevel || 'E0',
      lastTouch: o.lastTouch || acc?.lastTouch || '',
      nextAction: o.nextAction || '',
      nextActionDue: o.nextActionDue || '',
      overdue,
      score,
      why: whyOpportunity(o, { overdue, idle, consulting: !primaryEnabled, deal }),
    });
  }

  for (const a of accounts) {
    if (a.tier !== 'Tier A' && a.tier !== 'A') continue;
    if (statusRank(a.status) < 0) continue;
    if (opps.some((o) => o.accountId === a.id)) continue;
    const overdue = !!(asOf && a.nextActionDue && isAfter(asOf, a.nextActionDue));
    const idle = asOf && a.stageSince ? businessDaysBetween(a.stageSince, asOf) : 0;
    const score =
      statusRank(a.status) * 5 +
      Math.max(0, weightOf(a.evidenceLevel)) * 4 +
      (a.decisionMakerKnown ? 5 : 0) +
      (overdue ? 12 : 0) +
      Math.min(idle, 8) +
      (!primaryEnabled ? 8 : 0);
    rows.push({
      key: `acct-${a.id}`,
      kind: 'account',
      id: a.id,
      account: a.name || 'Unnamed account',
      title: 'No opportunity yet',
      stage: a.status,
      evidenceLevel: a.evidenceLevel || 'E0',
      lastTouch: a.lastTouch || '',
      nextAction: a.nextAction || '',
      nextActionDue: a.nextActionDue || '',
      overdue,
      score,
      why: whyAccount(a, { overdue, idle }),
    });
  }

  return rows.sort((x, y) => y.score - x.score).slice(0, limit);
}

function whyOpportunity(o, { overdue, idle, consulting = false, deal = null }) {
  if (consulting) {
    if (o.stage === 'Sprint SOW' || o.stage === 'Architecture upsell') return 'SOW / upsell path — highest package wedge priority.';
    if (o.stage === 'Proposal') return 'Proposal issued — advance toward SOW / deposit.';
    if (overdue) return 'Next action is overdue.';
    if (weightOf(o.evidenceLevel) >= 3) return 'The counterparty has already spent their own time or information on this.';
    if (idle > 5) return `Static for ${idle} business days.`;
    return 'Qualified package opportunity in motion.';
  }
  const label = deal?.label || 'Primary deal';
  const primaryKey = deal?.primaryKey || 'primaryD001';
  const candidateKey = deal?.candidateKey || 'd001Candidate';
  if (o[primaryKey]) return `${label}. Everything else is secondary to this.`;
  if (o[candidateKey]) return `${label} candidate — competing to become the first transaction.`;
  if (overdue) return 'Next action is overdue.';
  if (weightOf(o.evidenceLevel) >= 3) return 'The counterparty has already spent their own time or information on this.';
  if (idle > 5) return `Static for ${idle} business days.`;
  return 'Qualified opportunity in motion.';
}

function whyAccount(a, { overdue, idle }) {
  if (overdue) return 'Tier A with an overdue next action.';
  if (a.status === 'Discovery') return 'Discovery happened. It becomes an opportunity or it decays.';
  if (a.status === 'Engaged') return 'Engaged but not yet in a substantive commercial conversation.';
  if (idle > 3) return `Tier A, ${idle} business days without movement.`;
  return 'Tier A account requiring founder attention.';
}

/* ------------------------------------------------------ promotion rules -- */

export const PROMOTION_REASONS = [
  'Meaningful engagement occurred',
  'High-fit asset identified',
  'Warm introduction appeared',
  'Time-sensitive commercial signal',
  'Strategic value increased',
];

export const DEMOTION_REASONS = [
  'No response after a deliberate sequence',
  'Timing is wrong',
  'Sponsor authority is weak',
  'Use case is not real',
  'Inferior to stronger accounts',
];

// Accounts the AI-assisted (Tier B) lane suggests promoting, and Tier A
// accounts that have earned demotion. Suggestions only — a reason is required.
export function promotionCandidates(state, { accounts, opps }) {
  const promote = accounts
    .filter((a) => a.tier === 'Tier B' && statusRank(a.status) >= statusRank('Engaged'))
    .map((a) => ({ id: a.id, name: a.name, reason: 'Engagement has already occurred at Tier B.' }));

  const withOpp = new Set(opps.map((o) => o.accountId));
  const demote = accounts
    .filter((a) => a.tier === 'Tier A' && !withOpp.has(a.id) && statusRank(a.status) <= statusRank('Contacted'))
    .map((a) => ({ id: a.id, name: a.name, reason: 'Tier A with no engagement and no opportunity.' }));

  return { promote, demote };
}

export function qualificationCandidates(accounts = [], { cap = 15 } = {}) {
  const rows = [];
  for (const a of accounts || []) {
    if (a.tier === 'Tier A' || a.tier === 'Parked' || statusRank(a.status) < 0) continue;
    const classified = classifyAccount({
      country: a.country,
      city: a.city,
      segment: a.segment,
      industry: a.industry,
      crmStatus: a.crmStatus,
      crmAccountScore: a.crmAccountScore,
      facts: a.crmFacts,
      decisionMakerKnown: !!a.decisionMakerKnown || !!a.primaryContact,
    });
    const proposedTier = a.proposedTier && a.proposedTier !== 'Universe' ? a.proposedTier : classified.proposedTier;
    if (proposedTier !== 'Qualified Target' && proposedTier !== 'Tier A') continue;
    if (a.tier === proposedTier) continue;
    rows.push({
      id: a.id,
      name: a.name,
      currentTier: a.tier,
      proposedTier,
      reason: a.classificationWhy || classified.why,
      score: classified.score,
    });
  }
  rows.sort((x, y) => y.score - x.score);
  const tierA = rows.filter((r) => r.proposedTier === 'Tier A').slice(0, cap);
  const qualifiedTargets = rows.filter((r) => r.proposedTier === 'Qualified Target');
  return { qualifiedTargets, tierA, all: [...tierA, ...qualifiedTargets] };
}

/* ------------------------------------------------ primary designation ---- */

// Pure transform. Exactly one primary deal can exist; the chosen candidate is
// promoted to at least the primary stage and its history records the move.
export function withPrimary(opportunities, { candidateId, backupId = '', today, primaryStageLabel = 'Primary D001' }) {
  return (opportunities || []).map((o) => {
    const isChosen = o.id === candidateId;
    const promote = isChosen && (OPP_STAGE_RANK[o.stage] ?? -1) < (OPP_STAGE_RANK[primaryStageLabel] ?? OPP_STAGE_RANK['Primary D001'] ?? 5);
    return {
      ...o,
      primaryD001: isChosen,
      backupD001: backupId ? o.id === backupId : !!o.backupD001 && !isChosen,
      d001Candidate: isChosen ? true : !!o.d001Candidate,
      stage: promote ? primaryStageLabel : o.stage,
      stageSince: promote ? today : o.stageSince,
      history: promote ? [...(o.history || []), { stage: primaryStageLabel, date: today }] : o.history || [],
    };
  });
}

// Disposition of the outgoing Primary when it is replaced.
export function withReplacedPrimary(opportunities, { oldId, disposition, today }) {
  return (opportunities || []).map((o) => {
    if (o.id !== oldId) return o;
    const terminal = disposition === 'Lost' || disposition === 'Parked';
    return {
      ...o,
      primaryD001: false,
      backupD001: disposition === 'Backup',
      d001Candidate: disposition !== 'Lost',
      stage: terminal ? 'Won / Lost / Parked' : o.stage,
      stageSince: terminal ? today : o.stageSince,
      history: terminal ? [...(o.history || []), { stage: 'Won / Lost / Parked', date: today }] : o.history || [],
    };
  });
}

/* -------------------------------------------------- opportunity creation - */

// Spec §17: a booked meeting is not an opportunity.
export function opportunityReadiness(account) {
  const checks = [
    { key: 'problem', label: 'Problem or use case confirmed', ok: !!String(account?.problemConfirmed || '').trim() },
    { key: 'sponsor', label: 'Sponsor or client is plausible', ok: !!account?.decisionMakerKnown },
    { key: 'next', label: 'A next commercial step exists', ok: !!String(account?.nextAction || '').trim() },
  ];
  return { checks, ready: checks.every((c) => c.ok) };
}

export function isDubaiOpportunity(o) {
  return (o.market || 'Dubai') === 'Dubai';
}
